import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { customerPortalService } from '../services/customerPortalApi';
import toast from 'react-hot-toast';

const CustomerAuthContext = createContext();

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const getInitialCustomer = () => {
    const localCustomer = localStorage.getItem('customer-portal-user');
    if (localCustomer) {
      try {
        return JSON.parse(localCustomer);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const getInitialToken = () => {
    return localStorage.getItem('customer-portal-token') || null;
  };

  const [customer, setCustomer] = useState(getInitialCustomer);
  const [token, setToken] = useState(getInitialToken);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const refreshTimerRef = useRef(null);

  // Public route kontrolü
  const isPublicRoute = useMemo(() => {
    const publicRoutes = ['/login', '/musteri-portal/login'];
    return publicRoutes.some(route => location.pathname === route || location.pathname.startsWith(route));
  }, [location.pathname]);

  // Public route'larda loading'i başlangıçta false yap
  useEffect(() => {
    if (isPublicRoute) {
      setLoading(false);
    }
  }, [isPublicRoute]);

  // Check if customer is authenticated on mount
  useEffect(() => {
    // Public route'larda token kontrolü yapma ve loading'i false yap
    if (isPublicRoute) {
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Token geçerliliğini kontrol et
        const response = await customerPortalService.getCustomerInfo();
        if (response.success && response.data) {
          setCustomer(response.data);
          localStorage.setItem('customer-portal-user', JSON.stringify(response.data));
          // Token refresh mekanizması (opsiyonel - backend'de refresh endpoint varsa)
          scheduleProactiveRefresh(token);
        } else {
          // Token geçersizse temizle
          setCustomer(null);
          setToken(null);
          localStorage.removeItem('customer-portal-token');
          localStorage.removeItem('customer-portal-user');
        }
      } catch (error) {
        // Token geçersizse temizle
        setCustomer(null);
        setToken(null);
        localStorage.removeItem('customer-portal-token');
        localStorage.removeItem('customer-portal-user');
      }
      setLoading(false);
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isPublicRoute]);

  const scheduleProactiveRefresh = (jwtToken) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (!jwtToken) return;
    try {
      const [, payloadB64] = jwtToken.split('.');
      const payloadJson = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadJson);
      const expMs = (payload.exp || 0) * 1000;
      const thresholdMs = 5 * 60 * 1000; // 5 dakika önce
      const delay = expMs - Date.now() - thresholdMs;
      if (delay > 0) {
        refreshTimerRef.current = setTimeout(() => {
          // Token refresh için backend'de endpoint yoksa sadece kontrol et
          checkAuth();
        }, delay);
      }
    } catch (_) {
      // Eğer decode edilemezse zamanlayıcı kurmayalım
    }
  };

  const checkAuth = async () => {
    if (!token) return;
    try {
      const response = await customerPortalService.getCustomerInfo();
      if (response.success && response.data) {
        setCustomer(response.data);
        localStorage.setItem('customer-portal-user', JSON.stringify(response.data));
      } else {
        logout();
      }
    } catch (error) {
      logout();
    }
  };

  const login = async (portalUsername, portalPassword, rememberMe = true) => {
    try {
      setLoading(true);
      const response = await customerPortalService.login(portalUsername, portalPassword);
      
      if (response.success && response.data.token) {
        const newToken = response.data.token;
        const customerData = response.data.customer;
        
        setToken(newToken);
        setCustomer(customerData);
        
        // Token'ı localStorage'a kaydet (her zaman localStorage kullan, sessionStorage kullanma)
        localStorage.setItem('customer-portal-token', newToken);
        localStorage.setItem('customer-portal-user', JSON.stringify(customerData));
        
        scheduleProactiveRefresh(newToken);
        
        const isSubdomain = window.location.hostname === 'musteri.janjan.online';
        navigate(isSubdomain ? '/dashboard' : '/musteri-portal/dashboard');
      } else {
        throw new Error(response.message || 'Giriş başarısız');
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = (skipRedirect = false) => {
    setCustomer(null);
    setToken(null);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    localStorage.removeItem('customer-portal-token');
    localStorage.removeItem('customer-portal-user');
    
    // Public route'larda veya skipRedirect true ise login'e yönlendirme yapma
    if (!skipRedirect && !isPublicRoute) {
      const isSubdomain = window.location.hostname === 'musteri.janjan.online';
      navigate(isSubdomain ? '/login' : '/musteri-portal/login');
      toast.success('Çıkış yapıldı');
    }
  };

  const updateCustomerData = (patch = {}) => {
    setCustomer(prev => {
      const next = prev ? { ...prev, ...patch } : { ...patch };
      localStorage.setItem('customer-portal-user', JSON.stringify(next));
      return next;
    });
  };

  const value = {
    customer,
    token,
    loading,
    login,
    logout,
    updateCustomerData,
    isAuthenticated: !!token && !!customer,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
